import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Plus, Trash2, ChevronUp, ChevronDown, GripVertical, Save, Loader2,
  Heading, Type, Minus, Image as ImageIcon, ListChecks, CheckSquare, ListTodo,
  AlignLeft, Hash, Calendar, Clock, PenLine, Upload, Camera, Sparkles, Send, Columns3, Users,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useBusiness } from '../../contexts/BusinessContext';
import { getResourceRoles, type ResourceRole } from '../../lib/resourceRoles';
import {
  BLOCK_LABELS,
  CATEGORY_COLORS,
  aiEditBlock,
  cryptoId,
  createDocumentCategory,
  createDocumentTemplate,
  defaultBlockConfig,
  getDocumentTemplate,
  listDocumentCategories,
  saveDocumentAssignments,
  saveDocumentBlocks,
  updateDocumentTemplate,
  type DocumentAccessScope,
  type DocumentAssignment,
  type DocumentBlock,
  type DocumentBlockType,
  type DocumentCategory,
} from '../../lib/documents';

interface DocumentBuilderProps {
  businessId: string;
  templateId: string | null;
  /** Pre-populate a NEW document (e.g. from AI). Ignored when templateId is set. */
  initialDraft?: { title?: string; description?: string; suggestedCategory?: string; blocks?: DocumentBlock[] } | null;
  onClose: () => void;
  onSaved: () => void;
}

interface ResourceLite { id: string; first_name: string; last_name: string }

const BLOCK_GROUPS: { label: string; types: DocumentBlockType[] }[] = [
  { label: 'Content', types: ['heading', 'rich_text', 'image', 'divider'] },
  { label: 'Inputs', types: ['checklist', 'acknowledge', 'multiple_choice', 'short_text', 'long_text', 'number', 'date', 'time', 'table'] },
  { label: 'Proof', types: ['signature', 'additional_signatures', 'file_upload', 'photo'] },
];

const BLOCK_ICON: Record<DocumentBlockType, any> = {
  heading: Heading, rich_text: Type, divider: Minus, image: ImageIcon,
  checklist: ListChecks, acknowledge: CheckSquare, multiple_choice: ListTodo,
  short_text: AlignLeft, long_text: AlignLeft, number: Hash, date: Calendar,
  time: Clock, signature: PenLine, file_upload: Upload, photo: Camera, table: Columns3,
  additional_signatures: Users,
};

const HAS_REQUIRED: DocumentBlockType[] = [
  'checklist', 'acknowledge', 'multiple_choice', 'short_text', 'long_text',
  'number', 'date', 'time', 'signature', 'file_upload', 'photo', 'table', 'additional_signatures',
];

export default function DocumentBuilder({ businessId, templateId, initialDraft, onClose, onSaved }: DocumentBuilderProps) {
  const { selectedBusiness } = useBusiness();
  const [loading, setLoading] = useState(!!templateId);
  const [saving, setSaving] = useState(false);
  const preview = false; // editor is always in edit mode; preview lives on the document list
  const [mobilePaletteOpen, setMobilePaletteOpen] = useState(false);

  // Per-block AI editing
  const [aiBlockId, setAiBlockId] = useState<string | null>(null);
  const [aiInstruction, setAiInstruction] = useState('');
  const [aiBusyBlockId, setAiBusyBlockId] = useState<string | null>(null);

  const [id, setId] = useState<string | null>(templateId);
  const [title, setTitle] = useState(!templateId ? (initialDraft?.title || '') : '');
  const [description, setDescription] = useState(!templateId ? (initialDraft?.description || '') : '');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [categories, setCategories] = useState<DocumentCategory[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [accessScope, setAccessScope] = useState<DocumentAccessScope>('admin_only');
  const [requireSignature, setRequireSignature] = useState(false);
  const [blocks, setBlocks] = useState<DocumentBlock[]>(!templateId ? (initialDraft?.blocks || []) : []);
  const [assignments, setAssignments] = useState<DocumentAssignment[]>([]);

  const [roles, setRoles] = useState<ResourceRole[]>([]);
  const [resources, setResources] = useState<ResourceLite[]>([]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [rolesData, resourcesRes, categoriesData] = await Promise.all([
          getResourceRoles(businessId).catch(() => []),
          supabase
            ? supabase.from('resources').select('id, first_name, last_name').eq('business_id', businessId).order('first_name')
            : Promise.resolve({ data: [] as ResourceLite[] }),
          listDocumentCategories(businessId).catch(() => []),
        ]);
        if (!active) return;
        setRoles(rolesData as ResourceRole[]);
        setResources(((resourcesRes as any).data || []) as ResourceLite[]);
        setCategories(categoriesData as DocumentCategory[]);

        if (!templateId && initialDraft?.suggestedCategory) {
          const match = (categoriesData as DocumentCategory[]).find(
            (c) => c.name.toLowerCase() === initialDraft.suggestedCategory!.toLowerCase(),
          );
          if (match) setCategoryId(match.id);
        }

        if (templateId) {
          const full = await getDocumentTemplate(templateId);
          if (full && active) {
            setTitle(full.title);
            setDescription(full.description || '');
            setCategoryId(full.category_id || null);
            setTags(full.tags || []);
            setAccessScope(full.access_scope);
            setRequireSignature(!!full.settings?.require_signature_to_submit);
            setBlocks(full.blocks.map((b) => ({ ...b, id: b.id || cryptoId() })));
            setAssignments(full.assignments.map((a) => ({ ...a })));
          }
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [businessId, templateId]);

  const addBlock = (type: DocumentBlockType) => {
    setBlocks((prev) => [
      ...prev,
      { id: cryptoId(), sort_order: prev.length, type, config: defaultBlockConfig(type), required: false },
    ]);
  };

  const updateBlock = (blockId: string, patch: Partial<DocumentBlock>) => {
    setBlocks((prev) => prev.map((b) => (b.id === blockId ? { ...b, ...patch } : b)));
  };
  const updateConfig = (blockId: string, configPatch: Record<string, any>) => {
    setBlocks((prev) => prev.map((b) => (b.id === blockId ? { ...b, config: { ...b.config, ...configPatch } } : b)));
  };
  const removeBlock = (blockId: string) => setBlocks((prev) => prev.filter((b) => b.id !== blockId));

  const runBlockAI = async (block: DocumentBlock) => {
    if (!aiInstruction.trim() || aiBusyBlockId) return;
    setAiBusyBlockId(block.id);
    try {
      const config = await aiEditBlock({
        businessId,
        businessName: (selectedBusiness as any)?.name,
        type: block.type,
        config: block.config || {},
        instruction: aiInstruction.trim(),
      });
      updateBlock(block.id, { config });
      setAiInstruction('');
      setAiBlockId(null);
    } catch (e: any) {
      console.error('Block AI edit failed:', e);
      alert(e?.message || 'AI edit failed.');
    } finally {
      setAiBusyBlockId(null);
    }
  };
  const moveBlock = (index: number, dir: -1 | 1) => {
    setBlocks((prev) => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const addAssignment = () => {
    setAssignments((prev) => [...prev, { target_type: 'role', target_id: null, requirement: 'optional', recurrence: 'once' }]);
  };
  const updateAssignment = (idx: number, patch: Partial<DocumentAssignment>) => {
    setAssignments((prev) => prev.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  };
  const removeAssignment = (idx: number) => setAssignments((prev) => prev.filter((_, i) => i !== idx));

  const handleCreateCategory = async () => {
    const name = window.prompt('New category name');
    if (!name || !name.trim()) return;
    try {
      const color = CATEGORY_COLORS[categories.length % CATEGORY_COLORS.length];
      const cat = await createDocumentCategory(businessId, name.trim(), color);
      setCategories((prev) => [...prev, cat]);
      setCategoryId(cat.id);
    } catch (e) {
      console.error('Failed to create category:', e);
    }
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (!tags.includes(t)) setTags((prev) => [...prev, t]);
    setTagInput('');
  };

  const handleSave = async (publish: boolean) => {
    if (!title.trim()) { alert('Give the document a title first.'); return; }
    setSaving(true);
    try {
      let templateRowId = id;
      const settings = { require_signature_to_submit: requireSignature };

      if (!templateRowId) {
        const created = await createDocumentTemplate({
          business_id: businessId, title: title.trim(), description, category_id: categoryId, tags, access_scope: accessScope, settings,
        });
        templateRowId = created.id;
        setId(created.id);
      }

      await updateDocumentTemplate(templateRowId!, {
        title: title.trim(),
        description,
        category_id: categoryId,
        tags,
        access_scope: accessScope,
        settings,
        status: publish ? 'published' : 'draft',
      });
      await saveDocumentBlocks(templateRowId!, businessId, blocks);
      await saveDocumentAssignments(
        templateRowId!,
        businessId,
        accessScope === 'restricted' ? assignments.filter((a) => a.target_type === 'everyone' || a.target_id) : [],
      );

      onSaved();
      onClose();
    } catch (e: any) {
      console.error('Failed to save document:', e);
      alert(`Failed to save: ${e?.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  const content = (
    <div className="fixed inset-0 z-[100000] bg-gray-50 flex flex-col">
      {/* Header */}
      <div
        className="flex items-center justify-between gap-2 px-3 sm:px-6 py-3 bg-white border-b border-gray-200 shrink-0"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
      >
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 flex-shrink-0"><X className="h-5 w-5" /></button>
          <div className="min-w-0">
            <div className="text-[15px] font-semibold text-gray-900 truncate">{title || 'New Document'}</div>
            <div className="text-[12px] text-gray-400">{id ? 'Editing' : 'New document'}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={() => handleSave(false)} disabled={saving} className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-lg text-[13px] font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} <span className="hidden sm:inline">Save draft</span>
          </button>
          <button onClick={() => handleSave(true)} disabled={saving} className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg text-[13px] font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60">
            Publish
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center text-gray-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <div className="flex-1 overflow-hidden flex">
          {/* Palette */}
          {!preview && (
            <div className="w-56 shrink-0 border-r border-gray-200 bg-white overflow-y-auto p-3 hidden md:block">
              {BLOCK_GROUPS.map((group) => (
                <div key={group.label} className="mb-4">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5 px-1">{group.label}</div>
                  <div className="space-y-1">
                    {group.types.map((type) => {
                      const Icon = BLOCK_ICON[type];
                      return (
                        <button key={type} onClick={() => addBlock(type)} className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-[13px] text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors text-left">
                          <Icon className="h-4 w-4 flex-shrink-0" /> {BLOCK_LABELS[type]}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Canvas */}
          <div className="flex-1 overflow-y-auto">
            <div
              className="max-w-3xl mx-auto p-4 sm:p-6 space-y-4"
              style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 6rem)' }}
            >
              {/* Meta */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Document title" className="w-full text-lg font-semibold text-gray-900 outline-none placeholder-gray-300" />
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (optional)" rows={2} className="w-full text-sm text-gray-600 outline-none resize-none placeholder-gray-300" />
                <div className="flex flex-wrap items-center gap-2">
                  {/* Category */}
                  <select
                    value={categoryId || ''}
                    onChange={(e) => { if (e.target.value === '__new__') { handleCreateCategory(); } else { setCategoryId(e.target.value || null); } }}
                    className="text-[13px] bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-700"
                  >
                    <option value="">No category</option>
                    {categories.map((cat) => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                    <option value="__new__">+ New category…</option>
                  </select>
                  {categoryId && (() => {
                    const cat = categories.find((c) => c.id === categoryId);
                    return cat ? <span className="inline-flex items-center gap-1.5 text-[12px] px-2 py-1 rounded-full" style={{ backgroundColor: `${cat.color}1a`, color: cat.color }}><span className="h-2 w-2 rounded-full" style={{ backgroundColor: cat.color }} />{cat.name}</span> : null;
                  })()}
                </div>
                {/* Tags */}
                <div className="flex flex-wrap items-center gap-1.5">
                  {tags.map((tag) => (
                    <span key={tag} className="inline-flex items-center gap-1 text-[12px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">
                      #{tag}
                      <button onClick={() => setTags((prev) => prev.filter((t) => t !== tag))} className="text-gray-400 hover:text-red-500"><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                  <input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                    onBlur={addTag}
                    placeholder="Add tag…"
                    className="text-[12px] outline-none bg-transparent w-24 placeholder-gray-300"
                  />
                </div>
              </div>

              {/* Blocks */}
              {blocks.length === 0 && (
                <div className="text-center py-12 text-gray-400 text-sm border-2 border-dashed border-gray-200 rounded-xl">
                  Add blocks from the left to build your document.
                </div>
              )}

              {blocks.map((block, index) => (
                <div key={block.id} className="bg-white rounded-xl border border-gray-200 p-4 group">
                  {!preview && (
                    <div className="flex items-center justify-between mb-2.5">
                      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                        <GripVertical className="h-3.5 w-3.5" /> {BLOCK_LABELS[block.type]}
                      </div>
                      <div className="flex items-center gap-1">
                        {HAS_REQUIRED.includes(block.type) && (
                          <label className="flex items-center gap-1 text-[11px] text-gray-500 mr-2 cursor-pointer">
                            <input type="checkbox" checked={block.required} onChange={(e) => updateBlock(block.id, { required: e.target.checked })} className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600" />
                            Required
                          </label>
                        )}
                        {block.type !== 'divider' && (
                          <button
                            onClick={() => { setAiBlockId(aiBlockId === block.id ? null : block.id); setAiInstruction(''); }}
                            className={`inline-flex items-center gap-1 px-1.5 py-1 rounded text-[11px] font-semibold ${aiBlockId === block.id ? 'bg-violet-600 text-white' : 'text-violet-600 bg-violet-50 hover:bg-violet-100'}`}
                            title="Edit this section with AI"
                          >
                            <Sparkles className="h-3 w-3" /> AI
                          </button>
                        )}
                        <button onClick={() => moveBlock(index, -1)} className="p-1 rounded hover:bg-gray-100 text-gray-400"><ChevronUp className="h-4 w-4" /></button>
                        <button onClick={() => moveBlock(index, 1)} className="p-1 rounded hover:bg-gray-100 text-gray-400"><ChevronDown className="h-4 w-4" /></button>
                        <button onClick={() => removeBlock(block.id)} className="p-1 rounded hover:bg-red-50 text-red-400"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                  )}

                  {!preview && aiBlockId === block.id && (
                    <div className="mb-3 p-2.5 rounded-lg bg-violet-50 border border-violet-100">
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-violet-500 flex-shrink-0" />
                        <input
                          autoFocus
                          value={aiInstruction}
                          onChange={(e) => setAiInstruction(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); runBlockAI(block); } }}
                          disabled={aiBusyBlockId === block.id}
                          placeholder="Tell AI what to add/change in this section…"
                          className="flex-1 text-[13px] bg-white border border-violet-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-violet-500/30"
                        />
                        <button
                          onClick={() => runBlockAI(block)}
                          disabled={aiBusyBlockId === block.id || !aiInstruction.trim()}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-violet-600 text-white text-[12px] font-semibold hover:bg-violet-700 disabled:opacity-50"
                        >
                          {aiBusyBlockId === block.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                        </button>
                      </div>
                    </div>
                  )}

                  <BlockEditor block={block} preview={preview} onConfig={(p) => updateConfig(block.id, p)} />
                </div>
              ))}

              {/* Access & assignment */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="text-[13px] font-semibold text-gray-900 mb-3">Who can access this document?</div>
                <div className="space-y-2">
                  {([
                    ['admin_only', 'Admins only', 'Only admins can see it in the library'],
                    ['public', 'Public', 'Everyone in the business can view it'],
                    ['restricted', 'Assigned', 'Only specific roles or people'],
                  ] as [DocumentAccessScope, string, string][]).map(([val, label, hint]) => (
                    <label key={val} className="flex items-start gap-2.5 cursor-pointer">
                      <input type="radio" name="access" checked={accessScope === val} onChange={() => setAccessScope(val)} className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300" />
                      <div>
                        <div className="text-[13px] font-medium text-gray-800">{label}</div>
                        <div className="text-[12px] text-gray-400">{hint}</div>
                      </div>
                    </label>
                  ))}
                </div>

                {accessScope === 'restricted' && (
                  <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                    {assignments.map((a, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <select value={a.target_type} onChange={(e) => updateAssignment(idx, { target_type: e.target.value as any, target_id: null })} className="text-[13px] bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5">
                          <option value="role">Role</option>
                          <option value="resource">Person</option>
                          <option value="everyone">Everyone</option>
                        </select>
                        {a.target_type === 'role' && (
                          <select value={a.target_id || ''} onChange={(e) => updateAssignment(idx, { target_id: e.target.value || null })} className="flex-1 text-[13px] bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5">
                            <option value="">Select role…</option>
                            {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                          </select>
                        )}
                        {a.target_type === 'resource' && (
                          <select value={a.target_id || ''} onChange={(e) => updateAssignment(idx, { target_id: e.target.value || null })} className="flex-1 text-[13px] bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5">
                            <option value="">Select person…</option>
                            {resources.map((r) => <option key={r.id} value={r.id}>{r.first_name} {r.last_name}</option>)}
                          </select>
                        )}
                        {a.target_type === 'everyone' && <div className="flex-1 text-[13px] text-gray-400 px-2">All staff</div>}
                        <button onClick={() => removeAssignment(idx)} className="p-1.5 rounded hover:bg-red-50 text-red-400"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    ))}
                    <button onClick={addAssignment} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-blue-600 hover:text-blue-700">
                      <Plus className="h-4 w-4" /> Add assignment
                    </button>
                  </div>
                )}

                <label className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100 cursor-pointer">
                  <input type="checkbox" checked={requireSignature} onChange={(e) => setRequireSignature(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-blue-600" />
                  <span className="text-[13px] text-gray-700">Require a signature to submit this document</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile: Add block FAB (palette is hidden on small screens) */}
      {!loading && !preview && (
        <button
          onClick={() => setMobilePaletteOpen(true)}
          className="md:hidden fixed right-4 z-[100001] inline-flex items-center gap-1.5 px-4 py-3 rounded-full bg-blue-600 text-white text-[14px] font-semibold shadow-lg active:scale-95 transition-transform"
          style={{ bottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
        >
          <Plus className="h-5 w-5" /> Add block
        </button>
      )}

      {/* Mobile: Add block bottom sheet */}
      {mobilePaletteOpen && (
        <div className="md:hidden fixed inset-0 z-[100002] bg-black/40 flex items-end" onClick={() => setMobilePaletteOpen(false)}>
          <div
            className="bg-white rounded-t-2xl w-full max-h-[75vh] overflow-y-auto p-4"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[15px] font-semibold text-gray-900">Add a block</h3>
              <button onClick={() => setMobilePaletteOpen(false)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"><X className="h-5 w-5" /></button>
            </div>
            {BLOCK_GROUPS.map((group) => (
              <div key={group.label} className="mb-4">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5 px-1">{group.label}</div>
                <div className="grid grid-cols-2 gap-2">
                  {group.types.map((type) => {
                    const Icon = BLOCK_ICON[type];
                    return (
                      <button
                        key={type}
                        onClick={() => { addBlock(type); setMobilePaletteOpen(false); }}
                        className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-[13px] text-gray-700 bg-gray-50 hover:bg-blue-50 hover:text-blue-700 transition-colors text-left"
                      >
                        <Icon className="h-4 w-4 flex-shrink-0" /> {BLOCK_LABELS[type]}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return createPortal(content, document.body);
}

// ── Per-block editor / preview ───────────────────────────────
function BlockEditor({ block, preview, onConfig }: { block: DocumentBlock; preview: boolean; onConfig: (p: Record<string, any>) => void }) {
  const c = block.config || {};
  const ro = preview; // in preview, render the field as it appears to a filler (non-interactive scaffold)

  switch (block.type) {
    case 'heading':
      return ro
        ? <div className="text-lg font-bold text-gray-900">{c.text || 'Section heading'}</div>
        : <input value={c.text || ''} onChange={(e) => onConfig({ text: e.target.value })} placeholder="Section heading" className="w-full text-lg font-bold text-gray-900 outline-none placeholder-gray-300" />;
    case 'rich_text':
      return ro
        ? <div className="text-sm text-gray-700 whitespace-pre-wrap">{c.text || ''}</div>
        : <textarea value={c.text || ''} onChange={(e) => onConfig({ text: e.target.value })} placeholder="Instructions / text…" rows={3} className="w-full text-sm text-gray-700 outline-none resize-y placeholder-gray-300" />;
    case 'divider':
      return <hr className="border-gray-200" />;
    case 'image':
      return (
        <div className="space-y-2">
          {!ro && <input value={c.url || ''} onChange={(e) => onConfig({ url: e.target.value })} placeholder="Image URL" className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5" />}
          {c.url ? <img src={c.url} alt={c.caption || ''} className="max-h-48 rounded-lg" /> : ro ? <div className="text-xs text-gray-400">No image</div> : null}
          {!ro && <input value={c.caption || ''} onChange={(e) => onConfig({ caption: e.target.value })} placeholder="Caption (optional)" className="w-full text-xs text-gray-500 outline-none" />}
        </div>
      );
    case 'checklist':
      return (
        <div className="space-y-2">
          {!ro && <input value={c.label || ''} onChange={(e) => onConfig({ label: e.target.value })} placeholder="Checklist label" className="w-full text-sm font-medium text-gray-800 outline-none placeholder-gray-300" />}
          {ro && c.label && <div className="text-sm font-medium text-gray-800">{c.label}</div>}
          <div className="space-y-1.5">
            {(c.items || []).map((item: any, i: number) => (
              <div key={item.id} className="flex items-center gap-2">
                <input type="checkbox" disabled className="h-4 w-4 rounded border-gray-300" />
                {ro
                  ? <span className="text-sm text-gray-700">{item.text}</span>
                  : <>
                      <input value={item.text} onChange={(e) => { const items = [...c.items]; items[i] = { ...item, text: e.target.value }; onConfig({ items }); }} className="flex-1 text-sm border border-gray-200 rounded-lg px-2 py-1" />
                      <button onClick={() => onConfig({ items: c.items.filter((_: any, j: number) => j !== i) })} className="p-1 text-red-400 hover:bg-red-50 rounded"><Trash2 className="h-3.5 w-3.5" /></button>
                    </>}
              </div>
            ))}
          </div>
          {!ro && <button onClick={() => onConfig({ items: [...(c.items || []), { id: cryptoId(), text: '' }] })} className="text-[12px] font-medium text-blue-600 inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add item</button>}
        </div>
      );
    case 'acknowledge':
      return (
        <label className="flex items-start gap-2">
          <input type="checkbox" disabled className="mt-0.5 h-4 w-4 rounded border-gray-300" />
          {ro ? <span className="text-sm text-gray-700">{c.label}</span>
            : <input value={c.label || ''} onChange={(e) => onConfig({ label: e.target.value })} className="flex-1 text-sm outline-none" />}
        </label>
      );
    case 'multiple_choice':
      return (
        <div className="space-y-2">
          {!ro
            ? <input value={c.label || ''} onChange={(e) => onConfig({ label: e.target.value })} placeholder="Question" className="w-full text-sm font-medium text-gray-800 outline-none placeholder-gray-300" />
            : <div className="text-sm font-medium text-gray-800">{c.label}</div>}
          {!ro && (
            <label className="flex items-center gap-1.5 text-[12px] text-gray-500">
              <input type="checkbox" checked={!!c.multiple} onChange={(e) => onConfig({ multiple: e.target.checked })} className="h-3.5 w-3.5 rounded border-gray-300" /> Allow multiple
            </label>
          )}
          <div className="space-y-1.5">
            {(c.options || []).map((opt: any, i: number) => (
              <div key={opt.id} className="flex items-center gap-2">
                <input type={c.multiple ? 'checkbox' : 'radio'} disabled className="h-4 w-4 border-gray-300" />
                {ro ? <span className="text-sm text-gray-700">{opt.text}</span>
                  : <>
                      <input value={opt.text} onChange={(e) => { const options = [...c.options]; options[i] = { ...opt, text: e.target.value }; onConfig({ options }); }} className="flex-1 text-sm border border-gray-200 rounded-lg px-2 py-1" />
                      <button onClick={() => onConfig({ options: c.options.filter((_: any, j: number) => j !== i) })} className="p-1 text-red-400 hover:bg-red-50 rounded"><Trash2 className="h-3.5 w-3.5" /></button>
                    </>}
              </div>
            ))}
          </div>
          {!ro && <button onClick={() => onConfig({ options: [...(c.options || []), { id: cryptoId(), text: '' }] })} className="text-[12px] font-medium text-blue-600 inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add option</button>}
        </div>
      );
    case 'short_text':
    case 'long_text':
      return (
        <div className="space-y-1.5">
          {!ro ? <input value={c.label || ''} onChange={(e) => onConfig({ label: e.target.value })} placeholder="Question label" className="w-full text-sm font-medium text-gray-800 outline-none placeholder-gray-300" />
            : <div className="text-sm font-medium text-gray-800">{c.label}</div>}
          {block.type === 'short_text'
            ? <input disabled placeholder={c.placeholder || 'Short answer'} className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-gray-50" />
            : <textarea disabled placeholder={c.placeholder || 'Long answer'} rows={3} className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-gray-50 resize-none" />}
        </div>
      );
    case 'number':
      return (
        <div className="space-y-1.5">
          {!ro ? <input value={c.label || ''} onChange={(e) => onConfig({ label: e.target.value })} placeholder="Label" className="w-full text-sm font-medium text-gray-800 outline-none placeholder-gray-300" />
            : <div className="text-sm font-medium text-gray-800">{c.label}</div>}
          <div className="flex items-center gap-2">
            <input disabled type="number" className="w-32 text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-gray-50" />
            {!ro ? <input value={c.unit || ''} onChange={(e) => onConfig({ unit: e.target.value })} placeholder="Unit" className="w-24 text-xs border border-gray-200 rounded-lg px-2 py-1.5" /> : c.unit ? <span className="text-xs text-gray-500">{c.unit}</span> : null}
          </div>
        </div>
      );
    case 'date':
    case 'time': {
      const autoStamp = block.type === 'date' && !!c.autoStampAtCompletion;
      return (
        <div className="space-y-1.5">
          {!ro ? <input value={c.label || ''} onChange={(e) => onConfig({ label: e.target.value })} placeholder="Label" className="w-full text-sm font-medium text-gray-800 outline-none placeholder-gray-300" />
            : <div className="text-sm font-medium text-gray-800">{c.label}</div>}
          {autoStamp ? (
            <div className="inline-flex items-center gap-1.5 text-[12px] font-medium text-blue-600 bg-blue-50 border border-blue-100 rounded-lg px-2.5 py-1.5">
              <Clock className="h-3.5 w-3.5" /> Auto-stamps date &amp; time at completion
            </div>
          ) : (
            <input disabled type={block.type} className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-gray-50" />
          )}
          {!ro && block.type === 'date' && (
            <label className="flex items-center gap-2 text-[12px] text-gray-600 pt-1 cursor-pointer">
              <input type="checkbox" checked={autoStamp} onChange={(e) => onConfig({ autoStampAtCompletion: e.target.checked })} className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600" />
              Populate date &amp; time at completion
            </label>
          )}
        </div>
      );
    }
    case 'signature':
      return (
        <div className="space-y-1.5">
          {!ro ? <input value={c.label || ''} onChange={(e) => onConfig({ label: e.target.value })} placeholder="Signature label" className="w-full text-sm font-medium text-gray-800 outline-none placeholder-gray-300" />
            : <div className="text-sm font-medium text-gray-800">{c.label}</div>}
          <div className="h-20 border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center text-xs text-gray-400"><PenLine className="h-4 w-4 mr-1.5" /> Signature</div>
        </div>
      );
    case 'file_upload':
    case 'photo':
      return (
        <div className="space-y-1.5">
          {!ro ? <input value={c.label || ''} onChange={(e) => onConfig({ label: e.target.value })} placeholder="Label" className="w-full text-sm font-medium text-gray-800 outline-none placeholder-gray-300" />
            : <div className="text-sm font-medium text-gray-800">{c.label}</div>}
          <div className="h-16 border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center text-xs text-gray-400">
            {block.type === 'photo' ? <Camera className="h-4 w-4 mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}
            {block.type === 'photo' ? 'Add photo' : 'Upload file'}
          </div>
        </div>
      );
    case 'table': {
      const columns: { id: string; label: string }[] = Array.isArray(c.columns) ? c.columns : [];
      const rows: { id: string; cells: Record<string, string> }[] = Array.isArray(c.rows) ? c.rows : [];
      const setColumn = (i: number, label: string) => {
        const next = columns.map((col, j) => (j === i ? { ...col, label } : col));
        onConfig({ columns: next });
      };
      const addColumn = () => onConfig({ columns: [...columns, { id: cryptoId(), label: `Column ${columns.length + 1}` }] });
      const removeColumn = (id: string) => onConfig({
        columns: columns.filter((col) => col.id !== id),
        rows: rows.map((r) => { const cells = { ...r.cells }; delete cells[id]; return { ...r, cells }; }),
      });
      const setCell = (rowIdx: number, colId: string, val: string) => {
        const next = rows.map((r, j) => (j === rowIdx ? { ...r, cells: { ...r.cells, [colId]: val } } : r));
        onConfig({ rows: next });
      };
      const addRow = () => onConfig({ rows: [...rows, { id: cryptoId(), cells: {} }] });
      const removeRow = (id: string) => onConfig({ rows: rows.filter((r) => r.id !== id) });
      return (
        <div className="space-y-2">
          {!ro
            ? <input value={c.label || ''} onChange={(e) => onConfig({ label: e.target.value })} placeholder="Table label (e.g. Identify hazards)" className="w-full text-sm font-medium text-gray-800 outline-none placeholder-gray-300" />
            : c.label ? <div className="text-sm font-medium text-gray-800">{c.label}</div> : null}
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full text-[13px] border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  {columns.map((col, i) => (
                    <th key={col.id} className="border border-gray-200 p-1 align-top min-w-[120px]">
                      {ro ? (
                        <span className="text-gray-700 font-semibold px-1">{col.label}</span>
                      ) : (
                        <div className="flex items-center gap-1">
                          <input value={col.label} onChange={(e) => setColumn(i, e.target.value)} placeholder={`Column ${i + 1}`} className="flex-1 text-[12px] font-semibold text-gray-800 bg-white border border-gray-200 rounded px-1.5 py-1" />
                          {columns.length > 1 && <button onClick={() => removeColumn(col.id)} className="p-0.5 text-red-400 hover:bg-red-50 rounded"><Trash2 className="h-3 w-3" /></button>}
                        </div>
                      )}
                    </th>
                  ))}
                  {!ro && <th className="border border-gray-200 p-1 w-8"><button onClick={addColumn} title="Add column" className="p-0.5 text-blue-600 hover:bg-blue-50 rounded"><Plus className="h-3.5 w-3.5" /></button></th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rIdx) => (
                  <tr key={row.id}>
                    {columns.map((col) => (
                      <td key={col.id} className="border border-gray-200 p-1 align-top">
                        {ro ? (
                          <span className="text-gray-500 px-1">{row.cells?.[col.id] || ''}</span>
                        ) : (
                          <input value={row.cells?.[col.id] || ''} onChange={(e) => setCell(rIdx, col.id, e.target.value)} placeholder="Preset (optional)" className="w-full text-[12px] text-gray-700 border border-gray-100 rounded px-1.5 py-1" />
                        )}
                      </td>
                    ))}
                    {!ro && <td className="border border-gray-200 p-1 text-center">{rows.length > 1 && <button onClick={() => removeRow(row.id)} className="p-0.5 text-red-400 hover:bg-red-50 rounded"><Trash2 className="h-3.5 w-3.5" /></button>}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!ro && (
            <div className="flex items-center justify-between flex-wrap gap-2">
              <button onClick={addRow} className="text-[12px] font-medium text-blue-600 inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add row</button>
              <label className="flex items-center gap-2 text-[12px] text-gray-600 cursor-pointer">
                <input type="checkbox" checked={c.allowAddRows !== false} onChange={(e) => onConfig({ allowAddRows: e.target.checked })} className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600" />
                Let staff add rows when completing
              </label>
            </div>
          )}
        </div>
      );
    }
    case 'additional_signatures':
      return (
        <div className="space-y-1.5">
          {!ro
            ? <input value={c.label || ''} onChange={(e) => onConfig({ label: e.target.value })} placeholder="Section label (e.g. Additional signatures)" className="w-full text-sm font-medium text-gray-800 outline-none placeholder-gray-300" />
            : c.label ? <div className="text-sm font-medium text-gray-800">{c.label}</div> : null}
          <div className="rounded-lg border-2 border-dashed border-gray-200 px-3 py-2.5 flex items-center gap-2 text-xs text-gray-400">
            <Users className="h-4 w-4" /> Signers can add their name &amp; signature when completing
          </div>
        </div>
      );
    default:
      return null;
  }
}
